import React, { useState, useEffect, useCallback } from 'react';
import api, { adLabsAPI } from '../utils/api';
import { useAuth } from '../context/AuthContext';

const POLL_INTERVAL = 10000;

function copyToClipboard(text) {
  navigator.clipboard.writeText(text).catch(() => {});
}

function CopyButton({ value }) {
  const [copied, setCopied] = useState(false);
  const handle = () => {
    copyToClipboard(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button onClick={handle} style={{
      marginLeft: '0.5rem', padding: '2px 8px', fontSize: '0.75rem',
      background: copied ? '#16a34a' : '#e5e7eb', color: copied ? '#fff' : '#374151',
      border: 'none', borderRadius: '4px', cursor: 'pointer', transition: 'all 0.2s'
    }}>
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
}

function MonoRow({ label, value, copyable }) {
  return (
    <tr>
      <td style={{ padding: '6px 0', color: '#6B7280', fontSize: '0.85rem', whiteSpace: 'nowrap', paddingRight: '1rem' }}>{label}</td>
      <td style={{ padding: '6px 0' }}>
        <span style={{ fontFamily: 'monospace', fontSize: '0.9rem', color: '#111827', background: '#F3F4F6', padding: '2px 6px', borderRadius: '4px' }}>{value}</span>
        {copyable && <CopyButton value={value} />}
      </td>
    </tr>
  );
}

function TimeLeft({ expiresAt }) {
  const [remaining, setRemaining] = useState('');
  useEffect(() => {
    const calc = () => {
      const ms = new Date(expiresAt) - Date.now();
      if (ms <= 0) { setRemaining('Expired'); return; }
      const h = Math.floor(ms / 3600000);
      const m = Math.floor((ms % 3600000) / 60000);
      const s = Math.floor((ms % 60000) / 1000);
      setRemaining(`${h}h ${m}m ${s}s`);
    };
    calc();
    const t = setInterval(calc, 1000);
    return () => clearInterval(t);
  }, [expiresAt]);
  const ms = new Date(expiresAt) - Date.now();
  const color = ms < 1800000 ? '#dc2626' : ms < 3600000 ? '#d97706' : '#16a34a';
  return <span style={{ fontFamily: 'monospace', fontWeight: 700, color }}>{remaining}</span>;
}

function DockerChallengeCard({ session }) {
  const statusColor = session.containerStatus === 'running' ? '#16a34a' : '#d97706';

  return (
    <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '1.25rem', marginBottom: '1rem' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1rem' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700, color: '#111827' }}>{session.challengeTitle}</h2>
          <p style={{ margin: '0.25rem 0 0', color: '#6B7280', fontSize: '0.85rem', textTransform: 'capitalize' }}>
            {session.challengeCategory} challenge
          </p>
        </div>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', background: '#f0fdf4', color: statusColor, border: `1px solid ${statusColor}30`, borderRadius: '999px', padding: '3px 12px', fontSize: '0.8rem', fontWeight: 600 }}>
          <span style={{ width: 7, height: 7, borderRadius: '50%', background: statusColor, display: 'inline-block' }} />
          {session.containerStatus === 'running' ? 'Running' : 'Starting'}
        </span>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '2rem', background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '6px', padding: '0.75rem 1rem', marginBottom: '1rem' }}>
        <div>
          <div style={{ fontSize: '0.75rem', color: '#6B7280', marginBottom: '2px' }}>Time Remaining</div>
          <TimeLeft expiresAt={session.expiresAt} />
        </div>
        <div>
          <div style={{ fontSize: '0.75rem', color: '#6B7280', marginBottom: '2px' }}>Session ID</div>
          <code style={{ fontSize: '0.8rem', color: '#374151' }}>{session.sessionId}</code>
        </div>
      </div>

      {session.sessionUrl && (
        <a
          href={session.sessionUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'inline-block', padding: '0.5rem 1.5rem',
            background: '#1E40AF', color: '#fff', textDecoration: 'none',
            borderRadius: '6px', fontWeight: 600, fontSize: '0.875rem'
          }}
        >
          Open Challenge
        </a>
      )}
    </div>
  );
}

export default function MyLabPage() {
  const { user } = useAuth();
  const [adLab, setAdLab] = useState(null);
  const [adAccess, setAdAccess] = useState(null);
  const [dockerSessions, setDockerSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [connecting, setConnecting] = useState(false);

  const loadLabs = useCallback(async () => {
    try {
      const [adRes, dockerRes] = await Promise.allSettled([
        adLabsAPI.myLab(),
        api.get('/api/sessions/my-active')
      ]);

      // AD lab
      if (adRes.status === 'fulfilled') {
        const found = adRes.value.data?.lab || null;
        setAdLab(found);
        if (found && found.status === 'running') {
          try {
            const acc = await adLabsAPI.getAccess(found.instanceId);
            setAdAccess(acc.data);
          } catch (_) {}
        } else {
          setAdAccess(null);
        }
      }

      // Docker challenge sessions
      if (dockerRes.status === 'fulfilled') {
        setDockerSessions(dockerRes.value.data?.sessions || []);
      }

      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLabs();
    const t = setInterval(loadLabs, POLL_INTERVAL);
    return () => clearInterval(t);
  }, [loadLabs]);

  const openAdLabInBrowser = async () => {
    if (!adLab) return;
    setConnecting(true);
    try {
      const res = await adLabsAPI.connect(adLab.instanceId);
      window.open(res.data.url, '_blank');
    } catch (err) {
      alert('Could not get connection URL: ' + (err.response?.data?.error || err.message));
    } finally {
      setConnecting(false);
    }
  };

  const downloadVpn = async () => {
    if (!adLab) return;
    try {
      const res = await adLabsAPI.downloadVpn(adLab.instanceId);
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/x-openvpn-profile' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `sechire-lab-${adLab.instanceId}.ovpn`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert('VPN download failed: ' + (err.response?.data?.error || err.message));
    }
  };

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
      <div style={{ textAlign: 'center', color: '#6B7280' }}>Loading lab details...</div>
    </div>
  );

  if (error) return (
    <div style={{ maxWidth: 600, margin: '4rem auto', padding: '2rem', textAlign: 'center' }}>
      <div style={{ color: '#dc2626', marginBottom: '1rem' }}>Error: {error}</div>
      <button onClick={loadLabs} style={{ padding: '0.5rem 1.5rem', background: '#1E40AF', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>Retry</button>
    </div>
  );

  const hasAnything = adLab || dockerSessions.length > 0;

  if (!hasAnything) return (
    <div style={{ maxWidth: 600, margin: '4rem auto', padding: '2rem', textAlign: 'center' }}>
      <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>No Active Lab</div>
      <p style={{ color: '#6B7280' }}>You don't have a lab assigned. Contact your recruiter or admin to assign one.</p>
    </div>
  );

  const adStatusColor = adLab?.status === 'running' ? '#16a34a' : adLab?.status === 'provisioning' ? '#d97706' : '#dc2626';
  const adStatusLabel = adLab?.status === 'running' ? 'Running' : adLab?.status === 'provisioning' ? 'Provisioning...' : adLab?.status;

  return (
    <div style={{ maxWidth: 760, margin: '0 auto', padding: '2rem 1rem' }}>

      {/* Docker challenge sessions */}
      {dockerSessions.length > 0 && (
        <div style={{ marginBottom: adLab ? '2rem' : 0 }}>
          <h1 style={{ margin: '0 0 1.25rem', fontSize: '1.5rem', fontWeight: 700, color: '#111827' }}>My Lab</h1>
          {dockerSessions.map(s => (
            <DockerChallengeCard key={s.sessionId} session={s} />
          ))}
        </div>
      )}

      {/* AD Lab */}
      {adLab && (
        <div>
          {dockerSessions.length > 0 && (
            <h2 style={{ margin: '0 0 1rem', fontSize: '1.1rem', fontWeight: 600, color: '#374151', borderTop: '1px solid #E5E7EB', paddingTop: '1.5rem' }}>
              Active Directory Lab
            </h2>
          )}
          {dockerSessions.length === 0 && (
            <h1 style={{ margin: '0 0 0.25rem', fontSize: '1.5rem', fontWeight: 700, color: '#111827' }}>My Lab</h1>
          )}
          <p style={{ margin: '0 0 1.25rem', color: '#6B7280', fontSize: '0.9rem' }}>
            {adAccess?.scenarioLabel || adLab.scenario} &nbsp;&bull;&nbsp; Instance: <code style={{ fontSize: '0.85rem' }}>{adLab.instanceId}</code>
          </p>

          {/* Status bar */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '0.875rem 1.25rem', marginBottom: '1.5rem' }}>
            <div>
              <div style={{ fontSize: '0.75rem', color: '#6B7280', marginBottom: '2px' }}>Status</div>
              <div style={{ fontWeight: 700, color: adStatusColor }}>{adStatusLabel}</div>
            </div>
            {adLab.expiresAt && (
              <div>
                <div style={{ fontSize: '0.75rem', color: '#6B7280', marginBottom: '2px' }}>Time Remaining</div>
                <TimeLeft expiresAt={adLab.expiresAt} />
              </div>
            )}
            <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.75rem' }}>
              {adLab.status === 'running' && (
                <button onClick={openAdLabInBrowser} disabled={connecting} style={{
                  padding: '0.5rem 1.25rem', background: '#1E40AF', color: '#fff',
                  border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem'
                }}>
                  {connecting ? 'Opening...' : 'Open in Browser'}
                </button>
              )}
              {adLab.status === 'provisioning' && (
                <span style={{ color: '#d97706', fontSize: '0.875rem' }}>Lab is starting up, please wait...</span>
              )}
            </div>
          </div>

          {adLab.status === 'running' && adAccess && (
            <>
              <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '1.25rem', marginBottom: '1rem' }}>
                <h3 style={{ margin: '0 0 1rem', fontSize: '1rem', fontWeight: 600, color: '#111827' }}>Browser Access (Guacamole)</h3>
                <p style={{ margin: '0 0 0.75rem', color: '#6B7280', fontSize: '0.875rem' }}>Access the lab directly from your browser — no additional tools needed.</p>
                <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                  <tbody>
                    <MonoRow label="Username" value={adAccess.guacCredentials?.username || 'candidate'} copyable />
                    <MonoRow label="Password" value={adAccess.guacCredentials?.password || 'LabAccess2024!'} copyable />
                  </tbody>
                </table>
                <button onClick={openAdLabInBrowser} disabled={connecting} style={{
                  marginTop: '1rem', padding: '0.5rem 1.5rem', background: '#1E40AF', color: '#fff',
                  border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem'
                }}>
                  {connecting ? 'Opening...' : 'Open Lab in Browser'}
                </button>
              </div>

              {adAccess.sshEntry && (
                <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '1.25rem', marginBottom: '1rem' }}>
                  <h3 style={{ margin: '0 0 0.5rem', fontSize: '1rem', fontWeight: 600, color: '#111827' }}>SSH Access</h3>
                  {adAccess.sshEntry.note && <p style={{ margin: '0 0 0.75rem', color: '#6B7280', fontSize: '0.875rem' }}>{adAccess.sshEntry.note}</p>}
                  <div style={{ background: '#1e293b', borderRadius: '6px', padding: '0.875rem 1rem', marginBottom: '0.75rem', fontFamily: 'monospace', fontSize: '0.875rem', color: '#e2e8f0', userSelect: 'all' }}>
                    ssh {adAccess.sshEntry.username}@{adAccess.sshEntry.host}
                  </div>
                  <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                    <tbody>
                      <MonoRow label="Host" value={adAccess.sshEntry.host} copyable />
                      <MonoRow label="Port" value={String(adAccess.sshEntry.port || 22)} copyable />
                      <MonoRow label="Username" value={adAccess.sshEntry.username} copyable />
                      <MonoRow label="Password" value={adAccess.sshEntry.password} copyable />
                    </tbody>
                  </table>
                </div>
              )}

              {adAccess.networkMap && Object.keys(adAccess.networkMap).length > 0 && (
                <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '1.25rem', marginBottom: '1rem' }}>
                  <h3 style={{ margin: '0 0 1rem', fontSize: '1rem', fontWeight: 600, color: '#111827' }}>Network Map</h3>
                  <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                    <tbody>
                      {Object.entries(adAccess.networkMap).map(([name, ip]) => (
                        <MonoRow key={name} label={name} value={ip} copyable />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '1.25rem', marginBottom: '1rem' }}>
                <h3 style={{ margin: '0 0 0.5rem', fontSize: '1rem', fontWeight: 600, color: '#111827' }}>VPN Access</h3>
                <p style={{ margin: '0 0 0.75rem', color: '#6B7280', fontSize: '0.875rem' }}>
                  Download the OpenVPN config to access the lab network directly from your machine.
                </p>
                {adAccess.vpnReady ? (
                  <button onClick={downloadVpn} style={{
                    padding: '0.5rem 1.5rem', background: '#059669', color: '#fff',
                    border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem'
                  }}>
                    Download VPN Config (.ovpn)
                  </button>
                ) : (
                  <div style={{ color: '#d97706', fontSize: '0.875rem' }}>VPN config is still being generated, check back in a moment.</div>
                )}
              </div>

              <div style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '1.25rem' }}>
                <h3 style={{ margin: '0 0 0.75rem', fontSize: '0.9rem', fontWeight: 600, color: '#374151' }}>Instance Details</h3>
                <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                  <tbody>
                    <MonoRow label="Instance ID" value={adLab.instanceId} copyable />
                    <MonoRow label="Scenario" value={adAccess.scenarioLabel || adLab.scenario} />
                    <MonoRow label="Expires" value={new Date(adLab.expiresAt).toLocaleString()} />
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
